import { Box } from 'lucide-react';
import { cn } from '@strivelabs/ui-core/lib/utils';
import type { AttachedArtifact } from '@/types/artifact';
import { CARD, INK, INK_3, LINE, LINE_SOFT } from '../taskTheme';

/**
 * Above this, the tile shows an icon instead of the page.
 *
 * A thumbnail is a live render: the browser downloads the document to paint a
 * picture a quarter of its size. Worth it for a one-page brief, not for the
 * largest thing the agent can author, and a task can hold several at once.
 */
const THUMBNAIL_MAX_BYTES = 300_000;

interface TaskAttachmentCardProps {
  artifact: AttachedArtifact;
  onOpen: () => void;
}

/**
 * One artifact a task holds: a picture of the page over its title.
 *
 * The tile is the page itself, scaled down and inert — fully sandboxed, so it
 * paints its markup and runs none of its scripts, not focusable, and
 * transparent to the pointer so the whole card stays one button. Scripts are
 * for the artifact someone opened, not for a picture of it.
 */
export const TaskAttachmentCard = ({ artifact, onOpen }: TaskAttachmentCardProps) => {
  const showsPage = artifact.size_bytes <= THUMBNAIL_MAX_BYTES;

  return (
    <button
      type="button"
      onClick={onOpen}
      aria-label={`Open ${artifact.title}`}
      className={cn(
        CARD,
        LINE,
        'flex w-full flex-col overflow-hidden rounded-[10px] border text-left',
        'transition-[border-color,box-shadow]',
        // Written out rather than composed from the theme constant: Tailwind
        // reads source as text and never sees a class built at runtime.
        'hover:border-[rgba(0,0,0,0.14)] hover:shadow-[0_4px_14px_rgba(0,0,0,0.08)]',
      )}
    >
      <span
        className={cn(
          LINE_SOFT,
          'relative flex aspect-[4/3] w-full items-center justify-center overflow-hidden border-b bg-[#FBFBFB]',
        )}
      >
        {showsPage ? (
          <iframe
            src={artifact.url}
            title=""
            aria-hidden
            tabIndex={-1}
            loading="lazy"
            sandbox=""
            referrerPolicy="no-referrer"
            // Pinned to the tile's own corner, which is where the scale is
            // taken from. Left in the flow it would be centred, so a frame four
            // times the tile starts a tile and a half up and to the left, and
            // scaling from that corner puts the whole picture off the tile.
            className="pointer-events-none absolute left-0 top-0 h-[400%] w-[400%] origin-top-left scale-[0.25] border-0"
          />
        ) : (
          <Box className={cn(INK_3, 'size-7')} aria-hidden />
        )}
      </span>

      <span className="flex items-center gap-2 px-3 py-2.5">
        <Box className={cn(INK_3, 'size-[15px] shrink-0')} aria-hidden />
        <span className={cn(INK, 'truncate text-[13px] font-medium tracking-[-0.01em]')}>
          {artifact.title}
        </span>
      </span>
    </button>
  );
};
